import { type Type } from '@angular/core';

import { Widgets } from '@common/enums';

import type AccordionComponent from '@web-builder/widgets/accordion/accordion.component';
import type ButtonComponent from '@web-builder/widgets/button/button.component';
import type ChatBotComponent from '@web-builder/widgets/chat-bot/chat-bot.component';
import type CodeComponent from '@web-builder/widgets/code/code.component';
import type FormComponent from '@web-builder/widgets/form/form.component';
import type GalleryComponent from '@web-builder/widgets/gallery/gallery.component';
import type ImageComponent from '@web-builder/widgets/image/image.component';
import type IndentComponent from '@web-builder/widgets/indent/indent.component';
import type LineComponent from '@web-builder/widgets/line/line.component';
import type LoginEduComponent from '@web-builder/widgets/login-edu/login-edu.component';
import type LogoComponent from '@web-builder/widgets/logo/logo.component';
import type MapComponent from '@web-builder/widgets/map/map.component';
import type MenuComponent from '@web-builder/widgets/menu/menu.component';
import type NavigationComponent from '@web-builder/widgets/navigation/navigation.component';
import type PaymentFormComponent from '@web-builder/widgets/payment-form/payment-form.component';
import type SocialComponent from '@web-builder/widgets/social/social.component';
import type TestimonialComponent from '@web-builder/widgets/testimonial/testimonial.component';
import type TextComponent from '@web-builder/widgets/text/text.component';
import type TimerComponent from '@web-builder/widgets/timer/timer.component';
import type VideoComponent from '@web-builder/widgets/video/video.component';
import type ProductCardComponent from '@web-builder/widgets/product-card/product-card.component';
import type ShopCartComponent from '@web-builder/widgets/shop-cart/shop-cart.component';
import type ShopCartIconComponent from '@web-builder/widgets/shop-cart-icon/shop-cart-icon.component';
import type BeforeAfterComponent from '@web-builder/widgets/before-after/before-after.component';

type WidgetComponents =
    | IndentComponent
    | ButtonComponent
    | LineComponent
    | TextComponent
    | ImageComponent
    | AccordionComponent
    | MenuComponent
    | TimerComponent
    | ChatBotComponent
    | SocialComponent
    | VideoComponent
    | FormComponent
    | PaymentFormComponent
    | MapComponent
    | LoginEduComponent
    | GalleryComponent
    | CodeComponent
    | TestimonialComponent
    | LogoComponent
    | NavigationComponent
    | ProductCardComponent
    | ShopCartComponent
    | ShopCartIconComponent
    | BeforeAfterComponent;

type WidgetLoadingConfig =
    | {
          loadComponent(): Promise<{
              default: Type<WidgetComponents>;
          }>;
          component?: never;
      }
    | {
          loadComponent?: never;
          component: Type<WidgetComponents>;
      };

type WidgetConfigType = {
    [key in Widgets]: WidgetLoadingConfig;
};

const WidgetsConfig: Partial<WidgetConfigType> = {
    [Widgets.INDENT]: {
        loadComponent: () => import('./indent/indent.component'),
    },
    [Widgets.BUTTON]: {
        loadComponent: () => import('./button/button.component'),
    },
    [Widgets.LINE]: {
        loadComponent: () => import('./line/line.component'),
    },
    [Widgets.TEXT]: {
        loadComponent: () => import('./text/text.component'),
    },
    [Widgets.IMAGE]: {
        loadComponent: () => import('./image/image.component'),
    },
    [Widgets.ACCORDION]: {
        loadComponent: () => import('./accordion/accordion.component'),
    },
    [Widgets.MENU]: {
        loadComponent: () => import('./menu/menu.component'),
    },
    [Widgets.TIMER]: {
        loadComponent: () => import('./timer/timer.component'),
    },
    [Widgets.CHAT_BOTS]: {
        loadComponent: () => import('./chat-bot/chat-bot.component'),
    },
    [Widgets.SOCIAL_NETWORKS]: {
        loadComponent: () => import('./social/social.component'),
    },
    [Widgets.VIDEO]: {
        loadComponent: () => import('./video/video.component'),
    },
    [Widgets.FORM]: {
        loadComponent: () => import('./form/form.component'),
    },
    [Widgets.PAYMENT_FORM]: {
        loadComponent: () => import('./payment-form/payment-form.component'),
    },
    [Widgets.MAP]: {
        loadComponent: () => import('./map/map.component'),
    },
    [Widgets.LOGIN_EDU]: {
        loadComponent: () => import('./login-edu/login-edu.component'),
    },
    [Widgets.GALLERY]: {
        loadComponent: () => import('./gallery/gallery.component'),
    },
    [Widgets.CODE]: {
        loadComponent: () => import('./code/code.component'),
    },
    [Widgets.TESTIMONIAL]: {
        loadComponent: () => import('./testimonial/testimonial.component'),
    },
    [Widgets.LOGO]: {
        loadComponent: () => import('./logo/logo.component'),
    },
    [Widgets.NAVIGATION]: {
        loadComponent: () => import('./navigation/navigation.component'),
    },
    [Widgets.PRODUCT_CARD]: {
        loadComponent: () => import('./product-card/product-card.component'),
    },
    [Widgets.SHOP_CART]: {
        loadComponent: () => import('./shop-cart/shop-cart.component'),
    },
    [Widgets.SHOP_CART_ICON]: {
        loadComponent: () => import('./shop-cart-icon/shop-cart-icon.component'),
    },
    [Widgets.BEFORE_AFTER]: {
        loadComponent: () => import('./before-after/before-after.component'),
    },
};

export default WidgetsConfig;
